/**
 * BrowserHandoffPage — internal Dev proof surface for the browser handoff (#4780 A1).
 *
 * A browser task pauses on a test-store review; here a signed-in person approves it, cancels
 * it, or resets the saved login. Browser only: nothing is paid, ordered or charged.
 *
 * The one real click is "Get it": the authenticated approve. Starting the run that resumes the
 * browser is then this page's job, with or without that click's answer: whenever the server
 * itself reports an approval as approved with no dispatch on record, the page sends the
 * ordinary POST /api/users/exec into the approval's own session, naming the approval as its
 * `browser_handoff`. The server starts one run per approval and answers a repeat with the same
 * task, so that call may be repeated; nothing but the server's answer ever counts as approved.
 * See docs/plans/browser-handoff-a1.md §4.
 *
 * Every call goes through the existing authenticated `api` client; this page never touches a
 * token. Where `state` answers 404 the feature does not exist, and the page only says so.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../services/api';

const BASE = '/api/browser-handoff';
const POLL_MS = 4000;
const UNAVAILABLE_RETRY_MS = 30000;
// The exec endpoint requires a prompt; with `browser_handoff` the server uses its own text.
const RESUME_PROMPT = 'Resume the approved browser handoff.';
const IN_PROGRESS = 'handoff_dispatch_in_progress';
// An approve that got no answer is repeated (it is idempotent): twice more, 0.5 s and 1 s later.
const APPROVE_REPEATS = 2;
const APPROVE_BACKOFF_MS = 500;
// The execs this page sends by itself, the one that follows the click included: at most this
// many per approval and page, at least this far apart. After that only the manual control sends.
const AUTO_STARTS = 3;
const AUTO_START_GAP_MS = 3000;
// The client sends every call once; what is repeated, and when, is decided in this file.
const ONCE = { retries: 0 };
// A reset closes a live browser and deletes the saved login at the provider before it answers;
// the server gives that 30 s (HandoffLimits.reset_budget_s), the client's default is 5 s.
const RESET_ONCE = { retries: 0, timeout: 40000 };

const V = {
  bgCard: '#ffffff',
  border: '#d8d8dc',
  textDim: '#6e6e73',
  accent: '#5856d6',
  red: '#ff3b30',
  yellow: '#ff9500',
};
const card = { background: V.bgCard, border: `1px solid ${V.border}`, borderRadius: 10, padding: 16, marginBottom: 12 };
const heading = { fontSize: 14, fontWeight: 600, margin: '20px 0 8px' };
const dim = { fontSize: 12, color: V.textDim, marginTop: 4 };
const cell = { fontSize: 12, padding: '3px 12px 3px 0', textAlign: 'left', verticalAlign: 'top' };
const button = { padding: '6px 12px', borderRadius: 8, border: `1px solid ${V.border}`, background: V.bgCard, fontSize: 12, cursor: 'pointer' };
const primaryButton = { ...button, background: V.accent, borderColor: V.accent, color: '#fff' };
const pill = { padding: '2px 8px', borderRadius: 10, fontSize: 11, background: V.accent + '22', color: V.accent };

function errorCode(err) {
  const detail = err?.detail;
  if (detail && typeof detail === 'object' && typeof detail.code === 'string') return detail.code;
  if (err?.status === 404) return 'not_found';
  if (typeof detail === 'string' && detail) return detail;
  if (err?.status) return `http_${err.status}`;
  return err?.message || 'request_failed';
}

// The server answered and said no: a 4xx (the client reports a 401 as 'Unauthorized'). Any
// other failure — no connection, a timeout, a 5xx — says nothing about what happened.
function refused(err) {
  return err?.message === 'Unauthorized' || (err?.status >= 400 && err?.status < 500);
}

function post(path, body, config = ONCE) {
  return api.request(
    path, body === undefined ? { method: 'POST' } : { method: 'POST', body: JSON.stringify(body) }, config,
  );
}

const pause = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// The approve of one click. A refusal is final; an approve that got no answer is the same
// request again, a bounded number of times.
async function approve(approval) {
  for (let repeat = 0; ; repeat += 1) {
    try {
      return await post(`${BASE}/approvals/${approval.approval_ref}/approve`, {
        review_revision: approval.review_revision,
      });
    } catch (err) {
      if (refused(err) || repeat >= APPROVE_REPEATS) throw err;
      await pause(APPROVE_BACKOFF_MS * (repeat + 1));
    }
  }
}

function money(cents, currency) {
  return typeof cents === 'number' ? `${(cents / 100).toFixed(2)} ${currency || ''}`.trim() : '—';
}

function expiresIn(expiresAt, serverNowMs) {
  const left = Date.parse(expiresAt) - serverNowMs;
  if (!Number.isFinite(left)) return 'expiry unknown';
  if (left <= 0) return 'expired';
  const seconds = Math.ceil(left / 1000);
  const two = (n) => String(n).padStart(2, '0');
  return `expires in ${two(Math.floor(seconds / 60))}:${two(seconds % 60)}`;
}

function shown(value) {
  if (value === null || value === undefined) return '—';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

function runStarted(taskId) {
  return `Run started${taskId ? ` — task ${taskId}` : ''}. Its result will appear below.`;
}

// Where the run of an open review stands, and whether the manual start is offered (`retry`).
// What the server has on record comes first; `told` is what this page was told when it tried
// to start the run, for as long as the list does not show a dispatch.
function runStatus(approval, told) {
  const approved = approval.state === 'approved';
  if (approved && approval.dispatch_state !== 'none') {
    if (approval.dispatch_state === 'started') return { text: runStarted(approval.dispatch_task_id) };
    if (approval.dispatch_state === 'starting') return { text: 'Starting the run…' };
    if (approval.dispatch_state === 'unconfirmed') {
      return { text: 'Could not confirm that the run started — if nothing resumes, ask for a new approval.', error: true };
    }
    return { text: `Approved. Nothing is started from this page: the server reports the run as ${shown(approval.dispatch_state)}.` };
  }
  // Offered only where the list itself shows the review approved and not dispatched, and this
  // page sends nothing more by itself.
  const retry = approved && !!told?.spent;
  switch (told?.phase) {
    case 'sending':
      return { text: 'Approved. Starting the run…' };
    case 'started':
      return { text: runStarted(told.taskId) };
    case 'starting':
      return { text: 'Starting the run…', retry };
    case 'final':
      return { text: `The run was not started: ${told.code}.`, error: true };
    case 'refused':
      return {
        text: told.credits ? 'Not enough credits to start the run.' : `The run was not started: ${told.code}.`,
        error: true,
        retry,
      };
    case 'unknown':
      return told.spent
        ? { text: `Could not start the run (${told.code}). This page stopped trying by itself.`, error: true, retry }
        : { text: `Could not start the run yet (${told.code}). Trying again by itself…` };
    default:
      return approved ? { text: 'Approved. Starting the run…' } : null;
  }
}

function pairsTable(object) {
  const entries = Object.entries(object || {});
  if (entries.length === 0) return <div style={dim}>—</div>;
  return (
    <table style={{ borderCollapse: 'collapse', marginTop: 8 }}>
      <tbody>
        {entries.map(([key, value]) => (
          <tr key={key}>
            <td style={{ ...cell, color: V.textDim }}>{key}</td>
            <td style={cell}>{shown(value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function reviewTable(review) {
  const items = Array.isArray(review?.items) ? review.items : [];
  const currency = review?.currency;
  return (
    <table style={{ borderCollapse: 'collapse', marginTop: 8 }}>
      <thead>
        <tr>
          {['Item', 'Quantity', 'Unit price', 'Line total'].map((title) => (
            <th key={title} style={{ ...cell, color: V.textDim, fontWeight: 500 }}>{title}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map((item, index) => (
          <tr key={item.sku || index}>
            <td style={cell}>{item.name || item.sku}</td>
            <td style={cell}>{item.quantity}</td>
            <td style={cell}>{money(item.unit_price_cents, currency)}</td>
            <td style={cell}>{money(item.line_total_cents, currency)}</td>
          </tr>
        ))}
        <tr>
          <td style={{ ...cell, fontWeight: 600 }} colSpan={3}>Total</td>
          <td style={{ ...cell, fontWeight: 600 }}>{money(review?.total_cents, currency)}</td>
        </tr>
      </tbody>
    </table>
  );
}

function noteLine(note) {
  if (!note) return null;
  return <div role="status" style={{ ...dim, color: note.error ? V.red : V.textDim }}>{note.text}</div>;
}

function approvalHeader(approval) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontWeight: 600, fontSize: 13 }}>Review {approval.approval_ref}</span>
      <span style={pill}>{approval.state}</span>
    </div>
  );
}

export default function BrowserHandoffPage() {
  const [data, setData] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // The server's clock, so "expires in" does not depend on this machine's.
  const [clock, setClock] = useState({ now: 0, skewMs: 0 });
  const [busy, setBusy] = useState({});
  const [notes, setNotes] = useState({});
  // What this page was told when it tried to start an approval's run, by approval_ref.
  const [told, setTold] = useState({});
  // The guard behind that: per approval, whether an exec is out, how many this page sent by
  // itself, when the last one ended, and whether it has stopped sending.
  const starts = useRef(new Map());
  const inFlight = useRef(new Set());
  const latestLoad = useRef(0);
  const alive = useRef(false);

  // The only place the handoff exec is sent from — for the click, for a state answer and for
  // the manual control alike — so that one guard covers them all. It is the existing dispatch
  // (api.dispatchExec's URL) naming the approval, and it is safe to repeat.
  const startRun = useCallback(async (approval, manual = false) => {
    // Only what the server itself reports as approved, with no dispatch on record.
    if (approval?.state !== 'approved' || approval.dispatch_state !== 'none') return;
    const ref = approval.approval_ref;
    const start = starts.current.get(ref) || { sending: false, tries: 0, endedAt: -Infinity, stop: null };
    starts.current.set(ref, start);
    // One at a time, whoever asks; and nothing after the run exists or a final refusal.
    if (start.sending || start.stop === 'final') return;
    if (!manual) {
      if (start.stop || start.tries >= AUTO_STARTS) return;
      // Measured on a clock that only moves forward, from the end of the attempt before.
      if (performance.now() - start.endedAt < AUTO_START_GAP_MS) return;
    }
    const show = (answer) => {
      // `spent`: this page sends nothing more by itself; the manual control takes over.
      const spent = start.stop === 'manual' || start.tries >= AUTO_STARTS;
      setTold((prev) => ({ ...prev, [ref]: { ...answer, spent } }));
    };
    // Nothing can be sent for these, so nothing is counted; the next state answer asks again.
    const viUserId = api.getViUserId();
    if (!viUserId) {
      show({ phase: 'unknown', code: 'not_signed_in' });
      return;
    }
    if (!approval.session_id || !approval.review_revision) {
      show({ phase: 'final', code: 'unexpected_answer' });
      return;
    }

    start.sending = true;
    try {
      if (!manual) start.tries += 1;
      show({ phase: 'sending' });
      const answer = await post(`/api/users/exec?vi_user_id=${encodeURIComponent(viUserId)}`, {
        prompt: RESUME_PROMPT,
        session_id: approval.session_id,
        browser_handoff: { approval_ref: ref, review_revision: approval.review_revision },
      });
      if (answer?.ok === true && typeof answer.taskId === 'string' && answer.taskId) {
        // The run exists; a repeat would only name the same task.
        start.stop = 'final';
        show({ phase: 'started', taskId: answer.taskId });
      } else {
        start.stop = 'manual';
        show({ phase: 'refused', code: 'unexpected_answer' });
      }
    } catch (err) {
      const code = errorCode(err);
      if (err?.status === 409 && code === IN_PROGRESS) {
        // Not a failure: another request is starting it. The state says how that ends.
        show({ phase: 'starting' });
      } else if (err?.status === 404 || err?.status === 409) {
        start.stop = 'final';
        show({ phase: 'final', code });
      } else if (refused(err)) {
        // No credits (402), or a refusal the contract does not name: never repeated by itself.
        start.stop = 'manual';
        show({ phase: 'refused', code, credits: err.status === 402 });
      } else {
        // Nothing is known. The same call is sent again on a later state that still shows no
        // dispatch, within the bounds above.
        show({ phase: 'unknown', code });
      }
    } finally {
      start.sending = false;
      start.endedAt = performance.now();
    }
  }, []);

  const load = useCallback(async () => {
    const mine = ++latestLoad.current;
    try {
      const next = await api.request(`${BASE}/state`, {}, ONCE);
      if (mine !== latestLoad.current) return POLL_MS;
      const now = Date.now();
      const serverNow = Date.parse(next?.server_time);
      setClock({ now, skewMs: Number.isFinite(serverNow) ? serverNow - now : 0 });
      setData(next);
      setUnavailable(false);
      setLoadError(null);
      // An approved review without a dispatch is a click whose answer was lost, or a start
      // that never arrived: continue it, without another click. A page that is gone does not.
      if (alive.current) (next?.approvals || []).forEach((approval) => { startRun(approval); });
      return POLL_MS;
    } catch (err) {
      if (mine !== latestLoad.current) return POLL_MS;
      if (err?.status === 404) {
        setUnavailable(true);
        setData(null);
        return UNAVAILABLE_RETRY_MS;
      }
      setLoadError(errorCode(err));
      return POLL_MS;
    }
  }, [startRun]);

  useEffect(() => {
    let stopped = false;
    let timer = null;
    alive.current = true;
    const poll = async () => {
      const delay = await load();
      if (!stopped) timer = setTimeout(poll, delay);
    };
    poll();
    return () => { stopped = true; alive.current = false; clearTimeout(timer); };
  }, [load]);

  useEffect(() => {
    const timer = setInterval(() => setClock((prev) => ({ ...prev, now: Date.now() })), 1000);
    return () => clearInterval(timer);
  }, []);

  // One action at a time per key; its outcome (or its error code) is shown beside it.
  const run = useCallback(async (key, action) => {
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);
    setBusy((prev) => ({ ...prev, [key]: true }));
    setNotes((prev) => ({ ...prev, [key]: null }));
    try {
      const note = await action();
      setNotes((prev) => ({ ...prev, [key]: (typeof note === 'string' ? { text: note } : note) || null }));
    } catch (err) {
      setNotes((prev) => ({ ...prev, [key]: { text: `Failed: ${errorCode(err)}`, error: true } }));
    } finally {
      inFlight.current.delete(key);
      setBusy((prev) => ({ ...prev, [key]: false }));
      load();
    }
  }, [load]);

  // The one real click: the approve. The run is started from the server's answer to it — or,
  // when that answer is lost, from the state that shows the approval (see `load`).
  const getIt = (approval) => run(`approval:${approval.approval_ref}`, async () => {
    let answer;
    try {
      answer = await approve(approval);
    } catch (err) {
      if (refused(err)) throw err;
      return {
        text: `No answer to the approval (${errorCode(err)}). If it was recorded, the run starts by itself — otherwise click Get it again.`,
        error: true,
        whilePending: true,
      };
    }
    const approved = answer?.approval;
    // The answer has to be about what was clicked: the same approval, the same review revision.
    if (approved?.approval_ref !== approval.approval_ref || approved.review_revision !== approval.review_revision) {
      throw new Error('unexpected_answer');
    }
    if (approved.state === 'consumed') {
      // Not a failure: the click was made and its run already happened. The server answers an
      // approve this way once the review's resume has run — a late or repeated approve, say,
      // after a state answer started the run. Nothing is sent, and the review is shown the way
      // the list shows a consumed one, with its outcome.
      setData((prev) => prev && {
        ...prev,
        approvals: (prev.approvals || []).map((row) => (row.approval_ref === approved.approval_ref ? approved : row)),
      });
      return null;
    }
    if (approved.state !== 'approved') throw new Error('unexpected_answer');
    await startRun(approved);
    return null;
  });

  // Not an approval: the same exec, for a review the server already shows as approved.
  const startAgain = (approval) => run(`approval:${approval.approval_ref}`, () => startRun(approval, true));

  const cancel = (approval) => run(`approval:${approval.approval_ref}`, async () => {
    const answer = await post(`${BASE}/approvals/${approval.approval_ref}/cancel`);
    return answer?.already_canceled ? 'Already canceled.' : 'Canceled.';
  });

  const resetLogin = (profile) => {
    if (!window.confirm('Reset this saved login? The browser will have to sign in to the test store again, and open reviews for it are canceled.')) return;
    run(`profile:${profile.profile_ref}`, async () => {
      const answer = await post(`${BASE}/profiles/${profile.profile_ref}/reset`, undefined, RESET_ONCE);
      const canceled = `Canceled approvals: ${answer?.canceled_approvals ?? 0}.`;
      return answer?.status === 'reset_pending'
        ? `Reset started; the provider has not confirmed it yet. ${canceled}`
        : `Saved login reset. ${canceled}`;
    });
  };

  const fixture = (path, done) => run('fixture', async () => {
    await post(`${BASE}/fixture/${path}`);
    return done;
  });

  if (unavailable) {
    return <div style={{ padding: 40, color: V.textDim }}>Browser handoff is not available in this environment.</div>;
  }

  const approvals = data?.approvals || [];
  const open = approvals.filter((a) => a.state === 'pending' || a.state === 'approved');
  const others = approvals.filter((a) => a.state !== 'pending' && a.state !== 'approved');
  const profiles = data?.profiles || [];
  const serverNowMs = clock.now + clock.skewMs;
  // The note of an approve that got no answer is settled by the list: once the review shows as
  // anything but pending, the question it leaves open has its answer.
  const noteOf = (approval) => {
    const note = notes[`approval:${approval.approval_ref}`];
    return note?.whilePending && approval.state !== 'pending' ? null : note;
  };

  return (
    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Browser handoff (Dev)</h2>
      <div role="note" style={{ ...card, borderColor: V.yellow, background: V.yellow + '14', fontSize: 13, fontWeight: 600 }}>
        Internal Dev proof — browser only. Nothing is paid, ordered or charged.
      </div>
      {loadError && <div style={{ ...dim, color: V.red }}>Could not load: {loadError}</div>}
      {!data ? <div style={dim}>Loading…</div> : (
        <>
          <h3 style={heading}>Waiting for you</h3>
          {open.length === 0 && <div style={dim}>No review is waiting.</div>}
          {open.map((approval) => {
            const key = `approval:${approval.approval_ref}`;
            const status = runStatus(approval, told[approval.approval_ref]);
            return (
              <div key={approval.approval_ref} style={card}>
                {approvalHeader(approval)}
                <div style={dim}>Session {approval.session_id} · {expiresIn(approval.expires_at, serverNowMs)}</div>
                {reviewTable(approval.review)}
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  {/* Only a pending review can be approved; an approved one needs no second click. */}
                  {approval.state === 'pending' && (
                    <button type="button" style={primaryButton} disabled={!!busy[key]} onClick={() => getIt(approval)}>
                      Get it (browser-only, no payment)
                    </button>
                  )}
                  {status?.retry && (
                    <button type="button" style={button} disabled={!!busy[key]} onClick={() => startAgain(approval)}>
                      Try starting the run again
                    </button>
                  )}
                  <button type="button" style={button} disabled={!!busy[key]} onClick={() => cancel(approval)}>
                    Cancel
                  </button>
                </div>
                {noteLine(status)}
                {noteLine(noteOf(approval))}
              </div>
            );
          })}

          <h3 style={heading}>Other approvals</h3>
          {others.length === 0 && <div style={dim}>None yet.</div>}
          {others.map((approval) => (
            <div key={approval.approval_ref} style={card}>
              {approvalHeader(approval)}
              <div style={dim}>
                Session {approval.session_id} · Outcome: {approval.outcome || '—'}
                {approval.cancel_reason ? ` · ${approval.cancel_reason}` : ''}
              </div>
              {pairsTable(approval.evidence)}
              {noteLine(noteOf(approval))}
            </div>
          ))}

          <h3 style={heading}>Saved logins</h3>
          {profiles.length === 0 && <div style={dim}>No saved login.</div>}
          {profiles.map((profile) => {
            const key = `profile:${profile.profile_ref}`;
            return (
              <div key={profile.profile_ref} style={card}>
                {pairsTable({
                  Site: profile.site_key,
                  State: profile.state,
                  Generation: profile.generation,
                  'In use': profile.in_use ? 'yes' : 'no',
                })}
                <div style={{ marginTop: 12 }}>
                  <button type="button" style={button} disabled={!!busy[key]} onClick={() => resetLogin(profile)}>
                    Reset saved login
                  </button>
                </div>
                {noteLine(notes[key])}
              </div>
            );
          })}

          <h3 style={heading}>Test store</h3>
          <div style={card}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={button} disabled={!!busy.fixture} onClick={() => fixture('reprice', 'Prices changed.')}>
                Change prices (test)
              </button>
              <button type="button" style={button} disabled={!!busy.fixture} onClick={() => fixture('sign-out', 'Signed out everywhere.')}>
                Sign out everywhere (test)
              </button>
            </div>
            {noteLine(notes.fixture)}
            {data.fixture ? pairsTable(data.fixture) : <div style={dim}>Test store state is unavailable.</div>}
            <div style={dim}>Store home: {data.store?.home_url}</div>
          </div>
        </>
      )}
    </div>
  );
}
